import { createValidationEngine } from '@makemydeal/dr-shared-ui-utils';
import { contractFieldConfig } from './contractFieldConfig';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { getAccessoriesList } from '../selectors/accessoriesSelectors';
import { getLenderProgramName } from '../selectors/contractingSelectors';
import { getShopperInfo, getCoBuyerInfo } from '../selectors/offerRedux';
import * as customerSpdSelectors from '../selectors/customerSpdSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import {
    getSelectedGapProductsForContract,
    getContractApiErroredVPPs,
    withoutGapContractRequiredApiErrors
} from '../selectors/vehicleProtectionSelectors';
import type { ContractGapProduct, ContractApiErroredVPP } from '../selectors/vehicleProtectionSelectors';
import { getContractApiErroredVehicleInsurance } from '../selectors/contractingSelectors';
import type { ContractApiErroredVehicleInsurance } from '../selectors/contractingSelectors';
import { getVehicle } from '../selectors/vehicleSelectors';
import {
    getVehicleHasLiabilityCoverage,
    getVehicleInsuranceInfo,
    getRightToCancelDays,
    getRightToCancelCharge
} from '../selectors/BaseDealSelectors';
import { getIncludedTradeInsWithOriginalIndexes } from '../selectors/tradeIns';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getSelectedTermMonths } from '../selectors/offerSelectors';

export type ContractValidationInput = {
    buyer: { email?: string; state?: string; title?: string; [key: string]: any };
    coBuyer?: { email?: string; [key: string]: any } | null;
    gap?: ContractGapProduct[];
    contractApiErroredVPP?: ContractApiErroredVPP[];
    contractApiErroredVehicleInsurance?: ContractApiErroredVehicleInsurance | null;
    lender?: {
        name?: string;
        line1?: string;
        line2?: string;
        city?: string;
        postalCode?: string;
        phone?: string;
    } | null;
    accessories?: Array<{ name?: string; index: number }> | null;
    other?: Record<string, never>;
    tradeIns?: Array<{ vin?: string | null; amountOwed?: number; [key: string]: any }> | undefined;
    vehicle?: {
        bookoutOrInvoice?: {
            bookoutSource?: string;
            bookoutValue?: number;
            invoiceAmount?: number;
        };
        cylinders?: any;
        odometerMileageType?: any;
        hasLiabilityCoverage?: boolean;
        [key: string]: any;
    } | null;
    applicantEmails?: string[] | null;
    vehicleInsurance?: { policyNumber?: string; [key: string]: any } | null;
    terms?: number;
    cancellationTerms?: { rightToCancelDays?: number; rightToCancelCharge?: number };
};

function normalizeContractValidationInput(state: StateTree): ContractValidationInput {
    const buyerBase = getShopperInfo(state);
    const coBuyerBase = getCoBuyerInfo(state);
    const accessoriesArray = getAccessoriesList(state);
    const gapProducts = getSelectedGapProductsForContract(state);
    const gapProductCodes = new Set(gapProducts.map(({ productCode }) => productCode));
    const contractApiErroredVPP = getContractApiErroredVPPs(state).map((product) =>
        gapProductCodes.has(product.productCode) ? withoutGapContractRequiredApiErrors(product) : product
    );
    const lenderProgramName = getLenderProgramName(state);
    const contractApiErroredVehicleInsurance = getContractApiErroredVehicleInsurance(state);
    const isLenderAddressEnabled = Boolean(featureToggleSelectors.getTogglesWithoutSafetyChecks(state)?.enableLenderAddress);
    const titlingLender = isLenderAddressEnabled ? offerReduxSelectors.getTitlingLenderWithAddress(state) : undefined;
    const tradeVehicle = getVehicle(state);
    const hasLiabilityCoverage = getVehicleHasLiabilityCoverage(state);
    const tradeIns = getIncludedTradeInsWithOriginalIndexes(state);
    const vehicleInsuranceInfo = getVehicleInsuranceInfo(state);
    const rightToCancelDays = getRightToCancelDays(state);
    const rightToCancelCharge = getRightToCancelCharge(state);
    const { buyer: buyerSpd, coBuyer: coBuyerSpd } = customerSpdSelectors.getSpdDataForContracting(state);
    const dealTerm = getSelectedTermMonths(state);

    const buyer = { ...buyerBase, ...buyerSpd };
    const coBuyer = coBuyerBase ? { ...coBuyerBase, ...coBuyerSpd } : coBuyerBase;

    const normalizedAccessories = accessoriesArray?.map((acc: any) => ({
        name: acc.name,
        index: acc.originalIndex !== undefined ? acc.originalIndex : accessoriesArray.indexOf(acc)
    }));

    const applicantEmails = [];
    if (buyer.email) {
        applicantEmails.push(buyer.email);
    }
    if (coBuyer?.email) {
        applicantEmails.push(coBuyer.email);
    }

    const normalizedTradeIns = tradeIns.map((trade) => {
        const lienholderAddress = trade.tradeIn.lienHolderAddress || {};

        return {
            vin: trade.tradeIn.vin,
            vehicle: {
                year: trade.tradeIn.year ? trade.tradeIn.year.toString() : '',
                make: trade.tradeIn.make ?? '',
                model: trade.tradeIn.model ?? '',
                trim: trade.tradeIn.trim ?? ''
            },
            // Always include lienholder data
            amountOwed: trade.tradeIn.amountOwed || 0,
            lienholder: {
                name: trade.tradeIn.lienHolder || '',
                accountNumber: trade.tradeIn.lienHolderAccountNumber || '',
                streetAddress: lienholderAddress.address,
                city: lienholderAddress.city,
                state: lienholderAddress.stateProvince,
                zip: lienholderAddress.postalCode,
                phoneNumber: trade.tradeIn.lienHolderPhoneNumber || ''
            },
            index: trade.originalIndex,
            isDeleted: trade.isDeleted
        };
    });

    return {
        buyer,
        coBuyer,
        lender: {
            name: lenderProgramName,
            ...(titlingLender
                ? {
                      line1: titlingLender.line1 ?? undefined,
                      line2: titlingLender.line2 ?? undefined,
                      city: titlingLender.city ?? undefined,
                      postalCode: titlingLender.postalCode ?? undefined,
                      phone: titlingLender.phone ?? undefined
                  }
                : {})
        },
        gap: gapProducts.length > 0 ? gapProducts : undefined,
        contractApiErroredVPP: contractApiErroredVPP.length > 0 ? contractApiErroredVPP : undefined,
        accessories: normalizedAccessories && normalizedAccessories.length > 0 ? normalizedAccessories : null,
        contractApiErroredVehicleInsurance: contractApiErroredVehicleInsurance ?? undefined,
        vehicle: tradeVehicle
            ? {
                  bookoutOrInvoice: {
                      bookoutSource: tradeVehicle.wholesaleBookSource,
                      bookoutValue: tradeVehicle.wholesaleValue,
                      invoiceAmount: tradeVehicle.invoiceAmount
                  },
                  cylinders: tradeVehicle.cylinders,
                  odometerMileageType: tradeVehicle.odometerMileageType,
                  vehicleUse: tradeVehicle.vehicleUse,
                  principalDriverName: tradeVehicle.principalDriverName,
                  hasLiabilityCoverage,
                  fuelType: tradeVehicle.fuel
              }
            : undefined,
        vehicleInsurance: {
            policyNumber: vehicleInsuranceInfo?.policyNumber,
            liabilityCoverage: String(hasLiabilityCoverage),
            policyStartDate: vehicleInsuranceInfo?.policyStartDate,
            policyExpirationDate: vehicleInsuranceInfo?.policyExpirationDate
        },
        terms: dealTerm,
        tradeIns: normalizedTradeIns.length > 0 ? normalizedTradeIns : undefined,
        cancellationTerms: {
            rightToCancelDays,
            rightToCancelCharge
        }
    };
}

export function runContractValidations(state: StateTree): any {
    const input = normalizeContractValidationInput(state);
    const engine = createValidationEngine({
        initialConfig: contractFieldConfig,
        flow: 'contract',
        lockFlow: true
    });
    // Populate engine context
    engine.ctx.values = input;
    engine.ctx.offerType = offerReduxSelectors.getCurrentOfferType(state);
    engine.ctx.buyerState = input.buyer.state ?? null;
    engine.ctx.fuelType = input.vehicle?.fuelType ?? null;
    engine.ctx.featureToggles = featureToggleSelectors.getTogglesWithoutSafetyChecks(state) as Record<string, boolean>;
    // Evaluate and return GC-channel form errors
    engine.validateAll();
    return {
        errors: engine.getFormErrorState({ channel: 'gcalert', onlyActive: true }),
        hasErrors: engine.getHasFormErrors({ channel: 'gcalert', onlyActive: true })
    };
}

export function hasAllSpdData(state: StateTree): { hasBuyerData: boolean; hasCoBuyerData: boolean } {
    const buyerCommonConsumerId = offerReduxSelectors.getShopperCommonConsumerId(state);
    const coBuyerCommonConsumerId = offerReduxSelectors.getCoBuyerCommonConsumerId(state);

    const buyer = customerSpdSelectors.getCustomerAPISuccess(state, buyerCommonConsumerId);
    const coBuyer = coBuyerCommonConsumerId
        ? customerSpdSelectors.getCustomerAPISuccess(state, coBuyerCommonConsumerId)
        : undefined;

    return {
        hasBuyerData: Boolean(buyer),
        hasCoBuyerData: coBuyerCommonConsumerId ? Boolean(coBuyer) : true
    };
}
